import React, { useEffect, useState } from 'react';
import { Truck } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { useToast } from '@/components/ui/use-toast';
import DocumentUpload from '@/components/documents/DocumentUpload';
import DocumentList from '@/components/documents/DocumentList';
import StorageUsageIndicator from '@/components/documents/StorageUsageIndicator';
import * as documentsApi from '@/services/documentsService';

const VehicleDocumentsModal = ({ isOpen, onClose, vehicle }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const { toast } = useToast();

  const load = async () => {
    if (!vehicle?._id) return;
    try {
      setLoading(true);
      const data = await documentsApi.listDocuments({
        ownerType: 'vehicle',
        ownerId: vehicle._id,
      });
      setItems(data);
    } catch (e) {
      console.error(e);
      toast({
        title: 'Error',
        description: 'No se pudieron cargar los documentos del vehículo.',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isOpen && vehicle) {
      load();
    }
  }, [isOpen, vehicle]);

  const handleUploaded = () => {
    load();
    toast({
      title: 'Éxito',
      description: 'Documento/imagen subido correctamente.',
    });
  };

  const handleDownload = async (id) => {
    try {
      const url = await documentsApi.getPresignedDownload({ id });
      window.open(url, '_blank');
    } catch (e) {
      console.error(e);
      toast({
        title: 'Error',
        description: 'No se pudo descargar el documento.',
        variant: 'destructive',
      });
    }
  };

  const handleDelete = async (id) => {
    try {
      await documentsApi.deleteDocument({ id });
      await load();
      toast({
        title: 'Éxito',
        description: 'Documento eliminado correctamente.',
      });
    } catch (e) {
      console.error(e);
      toast({
        title: 'Error',
        description: 'No se pudo eliminar el documento.',
        variant: 'destructive',
      });
    }
  };

  const vehiculoLabel = vehicle?.placa
    ? `${vehicle?.nombre || 'Vehículo'} · ${vehicle.placa}`
    : vehicle?.nombre || 'Vehículo';

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Truck className="h-5 w-5 text-primary" />
            Documentos del Vehículo
          </DialogTitle>
          <DialogDescription>
            {vehiculoLabel}
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto py-4 space-y-4">
          <StorageUsageIndicator />

          <Card>
            <CardHeader className="py-3">
              <CardTitle className="text-base">Subir nuevo documento / imagen</CardTitle>
            </CardHeader>
            <CardContent>
              <DocumentUpload
                ownerType="vehicle"
                ownerId={vehicle?._id}
                onUploaded={handleUploaded}
              />
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="py-3">
              <CardTitle className="text-base">Listado de adjuntos</CardTitle>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="text-sm text-muted-foreground">Cargando documentos...</div>
              ) : (
                <DocumentList items={items} onDownload={handleDownload} onDelete={handleDelete} />
              )}
            </CardContent>
          </Card>
        </div>

        <div className="flex justify-end gap-2 pt-4 border-t">
          <Button variant="outline" onClick={onClose}>
            Cerrar
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default VehicleDocumentsModal;